import {
  createCameraLucidaInitialState,
} from './cameraLucidaModel'
import { describeCameraLucidaScope } from './cameraLucidaPresentation'
import type {
  CameraLucidaEvent,
  CameraLucidaExchangeEntry,
  CameraLucidaFinding,
  CameraLucidaPersistedInquiry,
  CameraLucidaSessionState,
} from '../types/cameraLucidaTypes'

const nextEntry = (
  state: CameraLucidaSessionState,
  entry: Omit<CameraLucidaExchangeEntry, 'id'>,
): CameraLucidaExchangeEntry => ({
  id: `entry-${state.exchange.length + 1}`,
  ...entry,
})

const unique = (values: readonly string[]): string[] => Array.from(new Set(values.filter(Boolean)))

const validFinding = (state: CameraLucidaSessionState, findingId: string): CameraLucidaFinding | null => (
  state.findings.find(finding => finding.id === findingId) || null
)

export const restoreCameraLucidaInquiry = (
  inquiry: CameraLucidaPersistedInquiry,
): CameraLucidaSessionState => {
  const initial = createCameraLucidaInitialState()
  const findings = Array.isArray(inquiry.findings) ? inquiry.findings : []
  const exchange: CameraLucidaExchangeEntry[] = [
    {
      id: 'entry-1',
      role: 'scholar',
      kind: 'question',
      text: inquiry.question,
    },
    ...findings.map((finding, index): CameraLucidaExchangeEntry => ({
      id: `entry-${index + 2}`,
      role: 'agent',
      kind: finding.suggested ? 'suggested' : 'filed',
      text: finding.text,
      findingId: finding.id,
    })),
    {
      id: `entry-${findings.length + 2}`,
      role: 'agent',
      kind: 'summary',
      text: inquiry.summary,
    },
  ]
  const filedCount = findings.filter(finding => !finding.suggested).length
  const suggestion = [...findings].reverse().find(finding => finding.suggested)?.scope || null

  return {
    ...initial,
    question: inquiry.question,
    status: 'idle',
    findings,
    suggestion,
    coverageRowIds: unique(findings.flatMap(finding => finding.scope.rowIds)),
    exchange,
    activity: {
      main: `ARCHIVED INQUIRY · ${filedCount} FINDINGS FILED`,
      units: '',
    },
    snapshotId: inquiry.snapshotId,
  }
}

export const reduceCameraLucidaState = (
  state: CameraLucidaSessionState,
  event: CameraLucidaEvent,
): CameraLucidaSessionState => {
  if (event.type === 'session.reset') {
    return createCameraLucidaInitialState()
  }

  if (event.type === 'inquiry.restored') {
    return restoreCameraLucidaInquiry(event.inquiry)
  }

  // camera_complete_inquiry can arrive before the model's assistant turn has
  // fully drained. Treat completion as terminal so trailing stream/tool events
  // cannot reopen the turn and strand the next question in the ask field.
  if (state.status === 'idle'
    && event.type !== 'inquiry.opened'
    && event.type !== 'finding.cited'
    && event.type !== 'finding.citation.cleared'
    && event.type !== 'finding.entered'
    && event.type !== 'finding.exited') {
    return state
  }

  if (event.type === 'inquiry.opened') {
    if (state.playing && state.status !== 'idle' && state.status !== 'error') return state
    return {
      ...state,
      question: event.question,
      status: 'thinking',
      playing: true,
      scope: null,
      suggestion: null,
      overlay: null,
      findings: [],
      coverageRowIds: [],
      live: '',
      activity: { main: 'INQUIRY OPENED', units: '' },
      citedFindingId: null,
      reentryFindingId: null,
      resumeAfterCitation: false,
      snapshotId: event.snapshotId || null,
      error: null,
      exchange: [
        ...state.exchange,
        nextEntry(state, { role: 'scholar', kind: 'question', text: event.question }),
      ],
    }
  }

  if (event.type === 'agent.progress') {
    return {
      ...state,
      status: 'thinking',
      live: event.text,
      activity: { main: event.activity, units: event.units || '' },
    }
  }

  if (event.type === 'agent.inspect') {
    return {
      ...state,
      scope: {
        rowIds: unique(event.scope.rowIds),
        witnessIds: unique(event.scope.witnessIds),
      },
      status: 'inspecting',
      live: event.text,
      activity: { main: event.activity, units: event.units || describeCameraLucidaScope(event.scope) },
    }
  }

  if (event.type === 'agent.remark') {
    return {
      ...state,
      status: 'thinking',
      live: '',
      activity: { main: event.activity || state.activity.main, units: state.activity.units },
      exchange: [...state.exchange, nextEntry(state, { role: 'agent', kind: 'remark', text: event.text })],
    }
  }

  if (event.type === 'agent.text.delta') {
    return { ...state, live: `${state.live}${event.text}`, status: 'thinking' }
  }

  if (event.type === 'agent.read') {
    return {
      ...state,
      scope: {
        rowIds: unique(event.scope.rowIds),
        witnessIds: unique(event.scope.witnessIds),
      },
      coverageRowIds: unique([...state.coverageRowIds, ...event.scope.rowIds]),
      status: 'reading',
      live: event.text || '',
      activity: {
        main: event.activity || 'READING THE COLLATION',
        units: describeCameraLucidaScope(event.scope),
      },
    }
  }

  if (event.type === 'agent.consult.characters') {
    return {
      ...state,
      overlay: {
        kind: 'characters',
        characterId: event.characterId,
        rowIds: unique(event.rowIds),
      },
      status: 'consulting',
      live: event.text || '',
      activity: { main: 'CONSULTING · CHARACTER INDEX', units: describeCameraLucidaScope({ rowIds: event.rowIds }) },
    }
  }

  if (event.type === 'agent.consult.closed') {
    return {
      ...state,
      overlay: null,
      status: 'thinking',
      live: '',
      activity: { main: 'INDEX CONSULTED', units: '' },
      exchange: event.text
        ? [...state.exchange, nextEntry(state, { role: 'agent', kind: 'remark', text: event.text })]
        : state.exchange,
    }
  }

  if (event.type === 'finding.filed' || event.type === 'reading.suggested') {
    const suggested = event.type === 'reading.suggested'
    const finding = { ...event.finding, suggested }
    const findings = state.findings.some(candidate => candidate.id === finding.id)
      ? state.findings.map(candidate => candidate.id === finding.id ? finding : candidate)
      : [...state.findings, finding]
    return {
      ...state,
      findings,
      suggestion: suggested ? finding.scope : state.suggestion,
      status: suggested ? 'thinking' : 'filing',
      live: '',
      activity: {
        main: `${suggested ? 'SUGGESTED READING' : 'FINDING FILED'} · ${finding.kind.toUpperCase()}`,
        units: describeCameraLucidaScope(finding.scope),
      },
      exchange: [
        ...state.exchange,
        nextEntry(state, {
          role: 'agent',
          kind: suggested ? 'suggested' : 'filed',
          text: finding.text,
          findingId: finding.id,
        }),
      ],
    }
  }

  if (event.type === 'inquiry.completed') {
    return {
      ...state,
      status: 'idle',
      playing: false,
      live: '',
      activity: {
        main: `INQUIRY CLOSED · ${state.findings.filter(finding => !finding.suggested).length} FINDINGS FILED`,
        units: '',
      },
      exchange: [...state.exchange, nextEntry(state, { role: 'agent', kind: 'summary', text: event.summary })],
    }
  }

  if (event.type === 'inquiry.failed') {
    return {
      ...state,
      status: 'error',
      playing: false,
      error: event.message,
      live: event.message,
    }
  }

  if (event.type === 'inquiry.paused') {
    if (!state.playing) return state
    return { ...state, playing: false, status: 'paused' }
  }

  if (event.type === 'inquiry.resumed') {
    if (state.playing || state.status !== 'paused') return state
    return { ...state, playing: true, status: 'thinking' }
  }

  if (event.type === 'finding.cited') {
    if (!validFinding(state, event.findingId)) return state
    return {
      ...state,
      citedFindingId: event.findingId,
      playing: false,
      resumeAfterCitation: state.citedFindingId ? state.resumeAfterCitation : state.playing,
    }
  }

  if (event.type === 'finding.citation.cleared') {
    if (!state.citedFindingId || state.reentryFindingId) return state
    const shouldResume = state.resumeAfterCitation && state.status !== 'idle' && state.status !== 'error'
    return {
      ...state,
      citedFindingId: null,
      playing: shouldResume,
      status: shouldResume && state.status === 'paused' ? 'thinking' : state.status,
      resumeAfterCitation: false,
    }
  }

  if (event.type === 'finding.entered') {
    if (!validFinding(state, event.findingId)) return state
    return {
      ...state,
      citedFindingId: event.findingId,
      reentryFindingId: event.findingId,
      playing: false,
      resumeAfterCitation: state.citedFindingId ? state.resumeAfterCitation : state.playing,
    }
  }

  if (event.type === 'finding.exited') {
    if (!state.reentryFindingId) return state
    return { ...state, reentryFindingId: null }
  }

  return state
}

export const cameraLucidaShownScope = (state: CameraLucidaSessionState) => {
  const findingId = state.reentryFindingId || state.citedFindingId
  if (findingId) return validFinding(state, findingId)?.scope || null
  return state.scope
}
